import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Coins, Home, Calculator, Info } from 'lucide-react';
import Seo from '@/components/Seo';

const formatINR = (n) => '₹' + Math.round(n).toLocaleString('en-IN');
const formatCr = (n) => {
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(2)} L`;
  return `₹${Math.round(n).toLocaleString('en-IN')}`;
};

const AffordabilityCalculatorPage = () => {
  const [income, setIncome] = useState(100000);
  const [expenses, setExpenses] = useState(40000);
  const [downPayment, setDownPayment] = useState(2000000);
  const [tenure, setTenure] = useState(20);
  const [rate, setRate] = useState(8.5);
  const [emiRatio, setEmiRatio] = useState(40); // % of net income

  const monthlySurplus = income - expenses;
  const maxEmi = (income * emiRatio) / 100;
  const safeEmi = Math.min(maxEmi, monthlySurplus * 0.7);

  // Loan amount from EMI: P = EMI * ((1+r)^n - 1) / (r * (1+r)^n)
  const r = rate / 100 / 12;
  const n = tenure * 12;
  const loanAmount = safeEmi > 0 && r > 0
    ? safeEmi * (Math.pow(1 + r, n) - 1) / (r * Math.pow(1 + r, n))
    : 0;
  const propertyValue = loanAmount + downPayment;

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-16" data-testid="affordability-page">
      <Seo title="Home Affordability Calculator" description="How much home can you afford? Our smart calculator factors in your income, expenses, and down payment to estimate your buying power." path="/affordability-calculator" />
      <div className="container-luxury max-w-5xl">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#D4AF37]/10 text-[#D4AF37] rounded-full text-xs font-medium mb-3">
            <Coins className="h-3.5 w-3.5" /> Smart planning tool
          </div>
          <h1 className="font-['Plus_Jakarta_Sans'] text-4xl md:text-5xl font-bold text-[#0F172A] mb-3">
            How much home can you afford?
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Get a realistic estimate of your buying power based on your income, savings, and lifestyle.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Inputs */}
          <div className="bg-white p-8 rounded-sm shadow-sm border border-border/50">
            <h2 className="font-['Plus_Jakarta_Sans'] text-2xl font-semibold text-[#0F172A] mb-6 flex items-center gap-2">
              <Calculator className="h-5 w-5 text-[#D4AF37]" /> Your Numbers
            </h2>

            <div className="space-y-5">
              <Field label="Monthly income (in-hand)" value={income} setValue={setIncome} prefix="₹" testid="income" />
              <Field label="Monthly fixed expenses" value={expenses} setValue={setExpenses} prefix="₹" testid="expenses" hint="Rent, EMIs, bills, groceries" />
              <Field label="Down payment available" value={downPayment} setValue={setDownPayment} prefix="₹" testid="downpayment" hint={`= ${formatCr(downPayment)}`} />

              <div className="grid grid-cols-2 gap-4">
                <Field label="Loan tenure" value={tenure} setValue={setTenure} suffix="yrs" testid="tenure" />
                <Field label="Interest rate" value={rate} setValue={setRate} suffix="%" testid="rate" step="0.1" />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm">EMI as % of income</Label>
                  <span className="text-sm font-bold text-[#D4AF37]">{emiRatio}%</span>
                </div>
                <input
                  type="range"
                  min="20"
                  max="60"
                  value={emiRatio}
                  onChange={(e) => setEmiRatio(Number(e.target.value))}
                  className="w-full accent-[#D4AF37]"
                  data-testid="emi-ratio-slider"
                />
                <div className="flex justify-between text-xs text-muted-foreground mt-1">
                  <span>Conservative</span>
                  <span>Aggressive</span>
                </div>
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="space-y-4">
            <div className="bg-[#0F172A] text-white p-8 rounded-sm shadow-md" data-testid="affordability-result">
              <p className="text-white/60 text-xs uppercase tracking-wider mb-2">You can afford a property worth</p>
              <p className="font-['Plus_Jakarta_Sans'] text-5xl md:text-6xl font-bold text-[#D4AF37] mb-2">
                {formatCr(propertyValue)}
              </p>
              <p className="text-white/70 text-sm">
                With a {formatCr(downPayment)} down payment and a {formatCr(loanAmount)} home loan
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Stat label="Comfortable EMI" value={formatINR(safeEmi)} sub="/ month" />
              <Stat label="Loan amount" value={formatCr(loanAmount)} sub={`@${rate}% / ${tenure}yr`} />
              <Stat label="Monthly surplus" value={formatINR(monthlySurplus)} sub="after expenses" />
              <Stat label="Total interest" value={formatCr(safeEmi * n - loanAmount)} sub="over loan tenure" />
            </div>

            <div className="bg-amber-50 border border-amber-200 p-4 rounded-sm flex gap-3 text-sm text-amber-900">
              <Info className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <p>
                <strong>Rule of thumb:</strong> Keep your EMI below 40% of monthly income for healthy finances. Add 5–10% buffer for stamp duty &amp; registration.
              </p>
            </div>

            <a href="/properties" className="block">
              <Button className="w-full bg-[#D4AF37] hover:bg-[#B8932F] text-[#0F172A] font-bold py-6">
                <Home className="h-4 w-4 mr-2" /> Browse properties under {formatCr(propertyValue)}
              </Button>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, value, setValue, prefix, suffix, testid, hint, step }) => (
  <div>
    <Label className="text-sm" htmlFor={testid}>{label}</Label>
    <div className="relative mt-1">
      {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">{prefix}</span>}
      <Input
        id={testid}
        type="number"
        step={step || '1'}
        value={value}
        onChange={(e) => setValue(Number(e.target.value) || 0)}
        className={`${prefix ? 'pl-7' : ''} ${suffix ? 'pr-12' : ''}`}
        data-testid={`afford-${testid}`}
      />
      {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">{suffix}</span>}
    </div>
    {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
  </div>
);

const Stat = ({ label, value, sub }) => (
  <div className="bg-white p-4 rounded-sm border border-border/50">
    <p className="text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
    <p className="font-bold text-[#0F172A] text-xl">{value}</p>
    <p className="text-xs text-muted-foreground">{sub}</p>
  </div>
);

export default AffordabilityCalculatorPage;
